import { useCallback, useEffect, useMemo, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Loader2, Users2, RefreshCw, Search, Workflow } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLOR = {
  ok: "bg-emerald-500",
  amber: "bg-amber-500",
  red: "bg-red-500",
  na: "bg-slate-300",
};

const UNASSIGNED_KEY = "__unassigned__";
const NO_PHASE_KEY = "__no_phase__";

// Canonical phase flow (used only when grouping by phase — sets column order)
const PHASE_ORDER = [
  "Propuesta",
  "Desarrollo",
  "Piloto",
  "Escalado",
  "Comercialización",
  "Completado",
  "Cerrado",
  "Descartado",
];

export function PMKanbanView({ authH, onSelectProject }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filterPhase, setFilterPhase] = useState("");
  const [filterLine, setFilterLine] = useState("");
  const [search, setSearch] = useState("");
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem("pg07_kanban_group_by") || "pm"); // pm | phase
  const [localColumns, setLocalColumns] = useState(null); // {colKey: [proj,...]}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/pg07/compliance/by-pm`, { headers: authH });
      const j = await r.json();
      setData(j);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [authH]);

  useEffect(() => { load(); }, [load]);

  // Build columns from data (either by PM or by phase)
  const columns = useMemo(() => {
    if (!data?.pms) return {};
    const cols = {};
    if (groupBy === "pm") {
      data.pms.forEach(pm => {
        const key = pm.pm_email || pm.pm_name || UNASSIGNED_KEY;
        cols[key] = {
          type: "pm",
          pm_email: pm.pm_email || null,
          pm_name: pm.pm_name || (key === UNASSIGNED_KEY ? null : key),
          display_name: pm.pm_name || pm.pm_email || "(sin PM)",
          projects: pm.projects || [],
        };
      });
    } else {
      // Group by phase — collect all projects across PMs into phase buckets, keep pm info on each card
      const flat = [];
      data.pms.forEach(pm => (pm.projects || []).forEach(p => {
        flat.push({ ...p, pm_email: pm.pm_email || null, pm_name: pm.pm_name || null });
      }));
      // Seed columns in canonical order
      PHASE_ORDER.forEach(ph => {
        cols[ph] = { type: "phase", phase: ph, display_name: ph, projects: [] };
      });
      // Then discover any additional phases from data
      flat.forEach(p => {
        const key = p.phase || NO_PHASE_KEY;
        if (!cols[key]) cols[key] = { type: "phase", phase: key === NO_PHASE_KEY ? null : key, display_name: key === NO_PHASE_KEY ? "(sin fase)" : key, projects: [] };
        cols[key].projects.push(p);
      });
    }
    return cols;
  }, [data, groupBy]);

  // Local view state syncs with server data unless user is in the middle of a drag
  useEffect(() => {
    if (!saving && data) setLocalColumns(columns);
  }, [columns, saving, data]);

  const view = localColumns || columns;

  const allPhases = new Set();
  const allLines = new Set();
  Object.values(view).forEach(col => (col.projects || []).forEach(p => {
    if (p.phase) allPhases.add(p.phase);
    if (p.business_line) allLines.add(p.business_line);
  }));

  const applyFilters = (projects) => {
    return (projects || []).filter(p => {
      if (filterPhase && p.phase !== filterPhase) return false;
      if (filterLine && p.business_line !== filterLine) return false;
      if (search && !p.name?.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const src = result.source.droppableId;
    const dst = result.destination.droppableId;
    if (src === dst && result.source.index === result.destination.index) return;

    const draggedProjectId = result.draggableId;
    const newCols = { ...view };
    const srcProjects = [...(newCols[src]?.projects || [])];
    const [moved] = srcProjects.splice(result.source.index, 1);
    if (!moved) return;
    newCols[src] = { ...newCols[src], projects: srcProjects };
    const dstProjects = [...(newCols[dst]?.projects || [])];
    dstProjects.splice(result.destination.index, 0, moved);
    newCols[dst] = { ...newCols[dst], projects: dstProjects };
    setLocalColumns(newCols);

    // PATCH the moved project
    if (src !== dst) {
      setSaving(true);
      try {
        const destCol = newCols[dst];
        if (destCol.type === "pm") {
          if (dst === UNASSIGNED_KEY) {
            toast.info("Para dejar sin PM, elimina la asignación desde 'Fichas PG-07' del proyecto.");
            setSaving(false);
            load();
            return;
          }
          const body = {};
          if (destCol.pm_email) body.pm_email = destCol.pm_email;
          if (destCol.pm_name) body.pm_name = destCol.pm_name;
          const r = await fetch(`${API}/pg07/projects/${draggedProjectId}`, {
            method: "PATCH",
            headers: { ...authH, "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          if (r.ok) {
            toast.success(`Movido a ${destCol.display_name}`);
          } else {
            const err = await r.json().catch(() => ({}));
            toast.error(err.detail || `Error moviendo (${r.status})`, { duration: 6000 });
          }
        } else if (destCol.type === "phase") {
          if (!destCol.phase) {
            toast.info("No se puede mover a '(sin fase)' — usa el editor del proyecto.");
            setSaving(false);
            load();
            return;
          }
          const r = await fetch(`${API}/pg07/projects/${draggedProjectId}`, {
            method: "PATCH",
            headers: { ...authH, "Content-Type": "application/json" },
            body: JSON.stringify({ phase: destCol.phase }),
          });
          if (r.ok) {
            toast.success(`Fase → ${destCol.display_name}`);
          } else {
            const err = await r.json().catch(() => ({}));
            toast.error(err.detail || `Error moviendo (${r.status})`, { duration: 6000 });
          }
        }
      } finally {
        setSaving(false);
        load();
      }
    }
  };

  if (loading) return <div className="p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  if (!data || !Object.keys(view).length) return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Sin datos.</CardContent></Card>;

  const orderedKeys = Object.keys(view).sort((a, b) => {
    if (groupBy === "pm") {
      if (a === UNASSIGNED_KEY) return -1;
      if (b === UNASSIGNED_KEY) return 1;
      return (view[a].display_name || "").localeCompare(view[b].display_name || "");
    }
    // groupBy === "phase" — respect canonical PHASE_ORDER, unknowns at end
    const ia = PHASE_ORDER.indexOf(a);
    const ib = PHASE_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  return (
    <div className="space-y-3" data-testid="pg07-kanban-view">
      <div className="flex items-center gap-2 flex-wrap p-3 bg-slate-50 dark:bg-slate-800/40 rounded border">
        <div className="flex items-center gap-1 bg-white dark:bg-slate-900 border rounded p-0.5" data-testid="pg07-kanban-group-toggle">
          <button
            onClick={() => { setGroupBy("pm"); localStorage.setItem("pg07_kanban_group_by", "pm"); }}
            className={`px-2.5 h-6 text-[11px] rounded flex items-center gap-1 ${groupBy === "pm" ? "bg-emerald-100 text-emerald-800 font-bold" : "text-muted-foreground hover:bg-slate-50"}`}
            data-testid="pg07-kanban-group-pm"
          ><Users2 className="w-3 h-3" />Por PM</button>
          <button
            onClick={() => { setGroupBy("phase"); localStorage.setItem("pg07_kanban_group_by", "phase"); }}
            className={`px-2.5 h-6 text-[11px] rounded flex items-center gap-1 ${groupBy === "phase" ? "bg-emerald-100 text-emerald-800 font-bold" : "text-muted-foreground hover:bg-slate-50"}`}
            data-testid="pg07-kanban-group-phase"
          ><Workflow className="w-3 h-3" />Por fase</button>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Filtrar:</span>
        <div className="flex items-center gap-1 border rounded px-2 h-7 bg-transparent">
          <Search className="w-3 h-3 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar proyecto..." className="text-xs bg-transparent outline-none w-40" data-testid="pg07-kanban-search" />
        </div>
        {groupBy === "pm" && (
          <select value={filterPhase} onChange={e => setFilterPhase(e.target.value)} className="h-7 text-xs rounded border px-2 bg-transparent" data-testid="pg07-kanban-filter-phase">
            <option value="">Todas las fases</option>
            {Array.from(allPhases).sort().map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
        <select value={filterLine} onChange={e => setFilterLine(e.target.value)} className="h-7 text-xs rounded border px-2 bg-transparent min-w-[180px]" data-testid="pg07-kanban-filter-line">
          <option value="">Todas las líneas</option>
          {Array.from(allLines).sort().map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        {(filterPhase || filterLine || search) && (
          <button className="text-xs text-emerald-700 hover:underline" onClick={() => { setFilterPhase(""); setFilterLine(""); setSearch(""); }} data-testid="pg07-kanban-clear">Limpiar</button>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          {groupBy === "pm" ? "Arrastra entre columnas para reasignar el PM" : "Arrastra entre columnas para mover el proyecto de fase"}
        </span>
        <Button size="sm" variant="ghost" onClick={load} data-testid="pg07-kanban-refresh"><RefreshCw className="w-3 h-3 mr-1" />Refrescar</Button>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: 500 }} data-testid="pg07-kanban-board">
          {orderedKeys.map(k => {
            const col = view[k];
            const filtered = applyFilters(col.projects);
            const isPmMode = col.type === "pm";
            const highlight = isPmMode ? (k === UNASSIGNED_KEY || !col.pm_email) : (k === NO_PHASE_KEY);
            const Icon = isPmMode ? Users2 : Workflow;
            return (
              <div key={k} className="flex-shrink-0 w-72" data-testid={`pg07-kanban-col-${k}`}>
                <div className={`rounded-t-lg px-3 py-2 border ${highlight ? "bg-amber-50 border-amber-300" : "bg-slate-100 dark:bg-slate-800 border-slate-300"}`}>
                  <div className="flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5" />
                    <span className="font-bold text-sm truncate flex-1" title={col.display_name}>{col.display_name}</span>
                    <Badge variant="outline" className="text-[10px]">{filtered.length}{filtered.length !== col.projects.length && `/${col.projects.length}`}</Badge>
                  </div>
                  {isPmMode && col.pm_email && <div className="text-[10px] text-muted-foreground truncate">{col.pm_email}</div>}
                </div>
                <Droppable droppableId={k}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`min-h-[300px] p-2 border border-t-0 rounded-b-lg space-y-1.5 transition-colors ${snapshot.isDraggingOver ? "bg-emerald-50 dark:bg-emerald-900/30 border-emerald-400" : "bg-white dark:bg-slate-900 border-slate-200"}`}
                    >
                      {filtered.map((proj, idx) => (
                        <Draggable key={proj.id} draggableId={proj.id} index={idx}>
                          {(prov, snap) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              {...prov.dragHandleProps}
                              className={`p-2 border rounded bg-white dark:bg-slate-800 cursor-grab active:cursor-grabbing transition-shadow ${snap.isDragging ? "shadow-lg border-emerald-400 rotate-1" : "shadow-sm hover:shadow-md"}`}
                              data-testid={`pg07-kanban-card-${proj.id}`}
                              onDoubleClick={() => onSelectProject?.(proj.id)}
                              title="Doble click para abrir fichas"
                            >
                              <div className="flex items-start gap-1.5">
                                <span className={`w-2 h-2 rounded-full ${STATUS_COLOR[proj.status] || "bg-slate-300"} flex-shrink-0 mt-1`} title={proj.status} />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-semibold leading-snug break-words">{proj.name}</div>
                                  <div className="text-[10px] text-muted-foreground truncate">
                                    {isPmMode
                                      ? `${proj.phase || "—"}${proj.business_line ? ` · ${proj.business_line}` : ""}`
                                      : `${proj.pm_name || proj.pm_email || "(sin PM)"}${proj.business_line ? ` · ${proj.business_line}` : ""}`}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {filtered.length === 0 && <div className="text-[10px] text-muted-foreground italic p-2 text-center">Sin proyectos</div>}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
}
